import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import PropertyAnalysis from "./PropertyAnalysis";
import PropertyDisclaimer from "./PropertyDisclaimer";
import ViewPropertyDetails, { ISnapshotTableData, TableData } from "../../../interface/Watchlist/propertyDetails";
import {
  ColumnType,
  COUNTRY,
  PASnapshotDataMap,
  PASuburbStatsAUMap,
  ShowHide_MSG,
  SnapshotDataMap,
  SuburbStatsAUMap,
  SuburbStatsNZMap,
  USER_DETAIL,
} from "../../../utils/constants";
import DataMapper from "../../../utils/dataMapper";
import { REIButton, REIDataTable } from "../../../components/ui";
import { httpPost } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import REITooltip from "../../../components/ui/tooltip";
import BoxComponent from "./BoxComponent";
import { IREIGraphColor, IREIGraphDataSet } from "../../../interface/graph";
import Graph from "../../../components/ui/graph";
import { formatCurrencyWithDecimal, formatNumberWithDecimal } from "../../../utils/commonUtil";
import { classNames } from "primereact/utils";
import MapWithMarker from "../../../components/shared/mapWithMarker";
import BasicFileUpload from "../../../components/shared/BasicFileUpload";
import PropertyDescription from "./PropertyDescription";

interface viewPropertyInformation {
  viewPropertyData: ViewPropertyDetails;
}

interface IMarker {
  lat: number;
  lng: number;
  title: string;
}

interface SnapshotDataResult {
  location: string;
  growthRate: string;
  useAnalysis: boolean;
  propertyType: string;
}

const PropertyInformation: React.FC<viewPropertyInformation> = React.memo(({ viewPropertyData }) => {
  const [showDetails, setShowDetails] = useState<boolean>();
  const [propertyInformation, setPropertyInformation] = useState<ViewPropertyDetails>(viewPropertyData);
  const [suburbStats, setSuburbStats] = useState<TableData[]>();
  const [isImageComponent, setIsImageComponent] = useState<boolean>(true);

  const graphValues = useMemo(() => {
    return propertyInformation?.MedianPriceChangeAU && propertyInformation?.MedianPriceChangeAU?.length > 0
      ? propertyInformation.MedianPriceChangeAU.map((value) => ({
          x: 0,
          y: value.PriceChange.Price,
          growth: value.PriceChange.Growth,
          label: value.PriceChange.Label,
        }))
      : [];
  }, [propertyInformation?.MedianPriceChangeAU]);
  const GrapahVisible = graphValues.length == 0 ? false : true;
  const [snapshotData, setSnapshotData] = useState<ISnapshotTableData[]>();
  const graphLabelText = useMemo(() => {
    return propertyInformation?.MedianPriceChangeAU && propertyInformation?.MedianPriceChangeAU?.length > 0
      ? propertyInformation.MedianPriceChangeAU.map((data) =>
          data.PriceChange.Label.includes("-") ? data.PriceChange.Label.replace(/[-,()]/g, "") : data.PriceChange.Label
        )
      : "";
  }, [propertyInformation?.MedianPriceChangeAU]);
  const MemebreDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [isDataNull, setIsDataNull] = useState<boolean>(false);
  const graphTitle = useRef<string>("");
  const graphLabelColor = useRef<string>("");
  const SnashotTooltipData = useRef<SnapshotDataResult>();
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [marker, setMarker] = useState<IMarker[]>([]);
  const zoom = 15;
  let quickAnalysisData;

  const getCoordinatesFromAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    const apiKey = process.env.GOOGLE_MAPS_API_KEY;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${apiKey}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch data from Google Maps API");
      }

      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    async function fetchData() {
      {
        const labelMap = viewPropertyData.isFromPropertyAnalyser
          ? PASuburbStatsAUMap
          : propertyInformation.PropertyDetails.Country == COUNTRY.AU
            ? SuburbStatsAUMap
            : SuburbStatsNZMap;
        const data = DataMapper({
          tableData: viewPropertyData.isFromPropertyAnalyser
            ? viewPropertyData.PAMarketingStatstics
            : viewPropertyData.PropertyStatistics,
          labelMap,
          propertyDetails: viewPropertyData.PropertyDetails,
        });

        if (data != undefined) {
          const orderedData = Array.from(labelMap.values()).map((key) => {
            const matchingItem = data.find((item) => item.title === key);
            return matchingItem || { key, title: labelMap.get(key), value: "N/A" };
          });

          setSuburbStats(orderedData);
          setPropertyInformation(viewPropertyData);
          GrapahVisible ? getGraphLabel() : "";
        }
      }
    }

    if (viewPropertyData != undefined) {
      fetchData();
      const googleMapsUrl = viewPropertyData?.GoogleMapsLinks?.GoogleMapsUrl?.split("?m=")[1];
      if (googleMapsUrl) {
        getCoordinatesFromAddress(googleMapsUrl).then((response) => {
          if (response) {
            setCenter(response);
            setMarker([{ lat: response.lat, lng: response.lng, title: googleMapsUrl }]);
          }
        });
      }
    }
  }, [viewPropertyData.PropertyDetails.PropertyId]);

  useEffect(() => {
    async function fetchSnapshotData() {
      if (!viewPropertyData.isFromPropertyAnalyser) {
        const parameter = {
          MemberId: propertyInformation.PropertyDetails.MemberId,
          MemberName: MemebreDetail.FirstName + " " + MemebreDetail.LastName,
          PropertyCountryCode: propertyInformation.PropertyDetails.Country,
          PropertyLinkId: propertyInformation.PropertyDetails.LinkIDHash,
          UserCountryCode: MemebreDetail.Country,
          Website: propertyInformation.PropertyDetails.Website,
          WebsiteMode: propertyInformation.PropertyDetails.WebsiteMode,
        };
        await httpPost(`${ApiEndPoints.analyser}/QuickAnalyseList2/`, parameter).then((response) => {
          if (response.status == 200) {
            if (response.data) {
              quickAnalysisData = Array.isArray(response.data) ? (response.data[0] ?? null) : response.data;
            }
          }
        });
      }

      const responseData = !viewPropertyData.isFromPropertyAnalyser
        ? Array.isArray(quickAnalysisData)
          ? quickAnalysisData[0]
          : quickAnalysisData
        : viewPropertyData.PASummarySnapshot;

      const labelMap = viewPropertyData.isFromPropertyAnalyser ? PASnapshotDataMap : SnapshotDataMap;

      const data = DataMapper({
        tableData: responseData,
        labelMap: labelMap,
        snapshotData: true,
      });

      const hasSnapshotData =
        !((!viewPropertyData.isFromPropertyAnalyser && !responseData?.DisplayGraph) ||
          data == undefined ||
          data?.length == 0);

      setIsDataNull(!hasSnapshotData);

      if (hasSnapshotData) {
        const index = data.findIndex((item) => item.title === "Cash flow p/a (pre-tax)");

        if (index > 0) {
          const [item] = data.splice(index, 1);
          data.unshift(item);
        }

        const formattedData = FormatSnapshotData(data);
        setSnapshotData(formattedData);
      }

      SnashotTooltipData.current = getSnapshotData(responseData);
    }

    if (viewPropertyData.PropertyDetails) {
      fetchSnapshotData();
    }
  }, [viewPropertyData.PropertyDetails.PropertyId]);

  const bodyClassName = (rowData) => {
    if (rowData && (rowData.includes("(") || rowData.includes(")"))) {
      return "text-red-500";
    }
    return "";
  };

  function getSnapshotData(data): SnapshotDataResult {
    if (!data) {
      return {
        location: "",
        growthRate: "",
        useAnalysis: false,
        propertyType: "",
      };
    }

    let propertyType = "";
    switch (data.LongTermGrowthPropertyType?.toLowerCase()) {
      case "house":
        propertyType = "houses";
        break;
      case "unit":
        propertyType = "units";
        break;
      default:
        propertyType = `${data.LongTermGrowthPropertyType?.toLowerCase()} properties`;
    }

    let location = "";
    let growthRate = "";
    let useAnalysis = true;

    const suburbRate = parseFloat(data.LongTermGrowthSuburbRate);
    const postcodeRate = parseFloat(data.LongTermGrowthPostcodeRate);
    const regionRate = parseFloat(data.LongTermGrowthRegionRate);

    if (!isNaN(suburbRate) && suburbRate < 10) {
      growthRate = `${suburbRate.toFixed(2)}%`;
      location = data.LongTermGrowthSuburb;
    } else if (!isNaN(postcodeRate) && postcodeRate < 10) {
      growthRate = `${postcodeRate.toFixed(2)}%`;
      location = data.LongTermGrowthPostcode;
    } else if (!isNaN(regionRate) && regionRate < 10) {
      growthRate = `${regionRate.toFixed(2)}%`;
      location = data.LongTermGrowthRegion;
    } else if (data.CountryCode === "NZ") {
      useAnalysis = false;
      growthRate = `${(parseFloat(data.UserCapitalGrowth) || 6).toFixed(2)}%`;
    } else {
      useAnalysis = false;
      growthRate = `${parseFloat(data.LongTermGrowthSuburbRate).toFixed(2)}%`;
    }

    return { location, growthRate, useAnalysis, propertyType };
  }

  const FormatSnapshotData = (snapshotData: ISnapshotTableData[]) => {
    let formattedData = [];

    snapshotData.map((snapshot) => {
      let formattedobject = {};

      for (const key in snapshot) {
        let formattedValue;
        let value = snapshot[key];
        const valueWithoutPercent = typeof snapshot[key] === "string" ? snapshot[key].replace(/%/g, "") : snapshot[key];

        if (value == "%" || value == "N/A" || value == "0" || value == "0.00" || value == "0.0000") {
          formattedValue = "";
        } else if (Number(value) < 0) {
          const absValue = Math.abs(Number(valueWithoutPercent));
          formattedValue =
            snapshot.title.includes("yield") || snapshot.title.includes("yield")
              ? `(${formatNumberWithDecimal(absValue, 0, 2) + "%"})`
              : `($${formatNumberWithDecimal(absValue, 0, 2)})`;
        } else if (Number(value) > 0) {
          formattedValue =
            snapshot.title.includes("yield") || snapshot.title.includes("yield")
              ? `${formatNumberWithDecimal(valueWithoutPercent, 0, 2) + "%"}`
              : (formattedValue = `${formatNumberWithDecimal(valueWithoutPercent, 0, 2)}`);
        } else if (isNaN(Number(value))) {
          formattedValue = value;
        }
        formattedobject[key] = formattedValue;
      }
      formattedData.push(formattedobject);
    });

    return formattedData;
  };

  const TooltipContent = (
    <div>
      <h2 className="text-sky-500">Median Listing Price Graph</h2>
      <p className="text-xs">
        The Median Listing Price Graph shows the historical median listing prices for the suburb / property type /
        bedrooms combination of the property, over the last 6 years and the previous 3 quarters.
        <br />
        Where there are insufficient listings the graph will rollup to dwelling type / suburb or just suburb.
      </p>
      <p className="bg-lime-300 p-1 text-gray-800">
        This shading is the graph at Suburb, Property Type and Bedrooms level
      </p>
      <p className="bg-yellow-200 p-1 text-gray-800"> This shading is the graph at Suburb and Property Type level</p>
      <p className="bg-orange-300 p-1 text-gray-800"> This shading is the graph at Suburb only level</p>
    </div>
  );

  const snapShotTooltipContent = (
    <div>
      <h2 className="text-cyan-600">Snapshot</h2>
      <p className="text-black text-sm">
        The snapshot is a summary view of how this property might perform over a 1yr and 10yr period. <br />
        It shows possible outcomes for pre tax cashflow, gross yields, and overall returns.
      </p>
      <p className="text-black text-sm">The analysis is calculated on the first available of:</p>
      <div className="bg-gray-50 h-8 content-center border-l-2 border-r-0 border-t-0 border-b-0 border-solid border-gray-400 text-sm">
        <span className="ml-5">
          Listed property price or median property price at suburb, property type and bedrooms.
        </span>
      </div>
      <p className="text-black text-sm">and the first available of:</p>
      <div className="bg-gray-50 h-8 content-center border-l-2 border-r-0 border-t-0 border-b-0 border-solid border-gray-400 text-sm">
        <span className="ml-5">
          Potential rent, percentile rent or median rent at the suburb, property type and bedrooms.
        </span>
      </div>
      <div className="long_term_growth_message text-sm">
        Overall returns include long term capital growth projections, and are based on{" "}
        {SnashotTooltipData.current?.useAnalysis ? (
          <span className="analysis">
            the latest analysis for <span className="property_type">{SnashotTooltipData.current.propertyType}</span> in{" "}
            <span className="suburb">{SnashotTooltipData.current.location}</span>
          </span>
        ) : (
          <span className="default">your default capital growth rate</span>
        )}{" "}
        of <span className="long_term_trend">{SnashotTooltipData.current?.growthRate}</span>
      </div>
    </div>
  );

  const snapShotTooltip = (
    <div>
      Snapshot
      <span>
        <REITooltip
          targetId={`snapshot-tooltip${viewPropertyData.PropertyDetails.LinkIDHash}`}
          content={snapShotTooltipContent}
          position="left"
        ></REITooltip>
        <i className={`pi pi-question-circle p-1 snapshot-tooltip${viewPropertyData.PropertyDetails.LinkIDHash}`} />
      </span>
    </div>
  );

  const renderGraphTitle = () => {
    return (
      <div className={`text-center text-sm font-semibold pt-1 pb-2 ${graphLabelColor.current}`}>
        <>
          <REITooltip targetId="median-graph-tooltip" content={TooltipContent} position="right"></REITooltip>
          Median Listing Price
          {!viewPropertyData.isFromPropertyAnalyser && <i className="pi pi-question-circle p-1 median-graph-tooltip" />}
          <br />
          {graphTitle.current}
        </>
      </div>
    );
  };

  const getGraphLabel = () => {
    if (propertyInformation.MedianPriceChangeAU.length != 0) {
      switch (propertyInformation.MedianPriceChangeAU[0].PriceChange.StatLevel) {
        case "4":
          graphTitle.current =
            propertyInformation.PropertyDetails.BedRooms +
            " b/r " +
            propertyInformation.PropertyDetails.Category.toLowerCase();
          graphLabelColor.current = "bg-green-300";
          break;
        case "3":
          graphTitle.current = propertyInformation.PropertyDetails.Category;
          graphLabelColor.current = "bg-yellow-200";
          break;
        case "2":
          graphTitle.current = propertyInformation.PropertyDetails.StatisticsSuburb.toUpperCase();
          graphLabelColor.current = "bg-orange-300";
          break;
        default:
          break;
      }
    }
  };

  const suburbStatsColumns = [
    {
      field: "title",
      position: "left",
    },
    {
      field: "data",
      position: "right",
    },
  ];

  const paSnapshotColumns = [
    {
      field: "title",
      position: "left",
      header: snapShotTooltip,
      width: "w-1/2",
    },
    {
      field: "ValueYR1",
      header: "Yr 1",
      position: "right",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },
    {
      field: "ValueYR3",
      header: "Yr 3",
      position: "right",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },
    {
      field: "ValueYR5",
      position: "right",
      header: "Yr 5",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },

    {
      field: "ValueYR10",
      position: "right",
      header: "Yr 10",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },
  ];

  const snapshotColumns = [
    {
      field: "title",
      position: "left",
      header: snapShotTooltip,
      width: "w-1/2",
    },
    {
      field: "ValueYR1",
      header: "Yr 1",
      position: "right",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },

    {
      field: "ValueYR10",
      position: "right",
      header: "Yr 10",
      type: ColumnType.negativeValues,
      width: "w-1/4",
      bodyClassName: bodyClassName,
    },
  ];

  const onShowPropertyDetails = () => {
    setShowDetails(!showDetails);
  };

  // Y-axis formatter function - moved outside useMemo to ensure it's properly defined
  const formatYAxisTick = useCallback((value: number) => {
    const num = Math.round(Number(value));
    const formatted = "$" + num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    return formatted;
  }, []);

  const customOptions = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const yValue = tooltipItem.dataset.data[tooltipItem.dataIndex];
              const growth =
                tooltipItem.dataset.extraData[tooltipItem.dataIndex] !== "N/A"
                  ? tooltipItem.dataset.extraData[tooltipItem.dataIndex]
                  : "";
              return [formatCurrencyWithDecimal(yValue), growth];
            },
          },
          multiLine: true,
        },
      },

      aspectRatio: 1.35,
      scales: {
        y: {
          grid: {
            display: false,
          },
          beginAtZero: true,
          ticks: {
            font: {
              size: 10,
            },
            callback: formatYAxisTick,
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 10,
            },
            autoSkip: false,
          },
        },
      },
    }),
    [formatYAxisTick]
  );

  const renderGraphColors = (data: any[]): IREIGraphColor => {
    const colors: string[] = [];
    const borders: string[] = [];
    let lastPrice = 0;

    for (const point of data) {
      if (point.label && point.label.includes("Mth Ago")) {
        colors.push("rgb(206, 205, 205)");
        borders.push("rgb(207, 206, 206)");
      } else {
        if (point.y > lastPrice) {
          colors.push("rgba(35,214,83,0.8)");
          borders.push("rgba(35,214,83,0.6)");
        } else {
          colors.push("rgba(240,83,67,255)");
          borders.push("rgba(241,84,68,256)");
        }
        lastPrice = point.y;
      }
    }

    return { backgroundColor: colors, borderColor: borders };
  };

  const renderDataSet = useCallback(() => {
    if (graphValues.length != 0) {
      const dataSet = [
        {
          extraData: graphValues.map((point) =>
            point.growth.includes("%")
              ? point.growth
              : point.growth == "N/A" ||
                  point.growth == null ||
                  point.growth == "" ||
                  point.growth == "0" ||
                  point.growth == "0.00"
                ? ""
                : `${formatNumberWithDecimal(Number(point.growth), 0, 2)}%`
          ),
          data: graphValues.map((point) => point.y),
          backgroundColor: renderGraphColors(graphValues).backgroundColor,
          borderColor: renderGraphColors(graphValues).borderColor,
        },
      ];
      return dataSet;
    } else {
      return [];
    }
  }, [graphValues]);

  const graphProps = useMemo(
    () => ({
      graphType: "bar" as const,
      dataSet: renderDataSet(),
      labels: graphLabelText,
      customOptions,
      customClass: "grow-0 justify-center",
      style: { borderRadius: "6px", height: "172px" },
    }),
    [renderDataSet, graphLabelText, customOptions]
  );

  return (
    <>
      <div className="flex lg:flex-row flex-col bg-gray-100">
        <BoxComponent cssClass="m-2">
          <div className="relative">
            {!viewPropertyData.isFromPropertyAnalyser && (
              <>
                <img
                  id="wli-icon-thumbnail"
                  data-testid="wli-icon-thumbnail"
                  src={propertyInformation.PropertyDetails.ThumbnailURL}
                  onClick={() => {
                    window.open(viewPropertyData.PropertyDetails.Link);
                  }}
                  className="w-full h-56 object-fill rounded-md cursor-pointer"
                />
                <div className="absolute bottom-1 w-full rounded-b-md bg-black bg-opacity-50 text-white text-center p-2">
                  <a
                    id="wli-lnk-viewproperty"
                    data-testid="wli-lnk-viewproperty"
                    href={viewPropertyData.PropertyDetails.Link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white no-underline text-xs md:text-sm"
                  >
                    View Property
                  </a>
                </div>
              </>
            )}

            {isImageComponent && viewPropertyData.isFromPropertyAnalyser && (
              <BasicFileUpload propertyData={viewPropertyData.PropertyDetails.WatchlistID}></BasicFileUpload>
            )}

            {!isImageComponent && center && (
              <div className="w-full h-56 object-fill">
                <MapWithMarker center={center} markers={marker} zoom={zoom} />
              </div>
            )}

            {viewPropertyData.isFromPropertyAnalyser && (
              <div className="absolute bottom-0 right-0 flex space-x-1">
                <REIButton
                  id="wli-btn-viewimage"
                  icon="pi pi-image"
                  label=""
                  tooltip="view image"
                  tooltipOptions={{ position: "bottom" }}
                  onClick={() => setIsImageComponent(true)}
                ></REIButton>
                <REIButton
                  id="wli-btn-viewmap"
                  icon="pi pi-map"
                  tooltip="view map"
                  tooltipOptions={{ position: "bottom" }}
                  classNames="ml-1"
                  label=""
                  onClick={() => setIsImageComponent(false)}
                ></REIButton>
              </div>
            )}
          </div>
        </BoxComponent>
        <BoxComponent cssClass="m-2 ml-0">
          <div className="text-xs md:text-sm font-bold">
            {`${viewPropertyData.PropertyDetails.Suburb} ${
              viewPropertyData.PropertyDetails.BedRooms != null &&
              viewPropertyData.PropertyDetails.BedRooms != "-" &&
              viewPropertyData.PropertyDetails.BedRooms != undefined
                ? viewPropertyData.PropertyDetails.BedRooms
                : "-"
            } b/r ${viewPropertyData.PropertyDetails.Category.toLowerCase()} ${" "}Statistics`}
            <div className="panel-box">
              <REIDataTable
                data={suburbStats}
                columns={suburbStatsColumns}
                customclass="hide-datatable-header"
              ></REIDataTable>
            </div>
          </div>
        </BoxComponent>
        <BoxComponent cssClass="m-2 ml-0" key={propertyInformation.PropertyDetails.LinkIDHash}>
          {GrapahVisible && (
            <>
              {renderGraphTitle()}
              <Graph {...graphProps} id="wli-graph-medianprice" />
            </>
          )}
          {!GrapahVisible && (
            <div>
              <div className="text-xs md:text-sm p-2 bg-zinc-300 mb-1" hidden={GrapahVisible}>
                {`No median price graph.`}
                <br />
                {` There isn't sufficient data to provide a graph.`}
              </div>
            </div>
          )}
        </BoxComponent>

        <BoxComponent cssClass="m-2 ml-0 align-baseline  overflow-scroll">
          {snapshotData && (
            <div className="panel-box w-full" hidden={isDataNull}>
              <REIDataTable
                data={snapshotData}
                columns={viewPropertyData.isFromPropertyAnalyser ? paSnapshotColumns : snapshotColumns}
                customclass="leading-3"
              ></REIDataTable>
            </div>
          )}
          {isDataNull && (
            <div className="panel-box h-56 content-center text-sm md:text-base p-2 bg-zinc-300 mb-1">
              No snapshot table.There isn't sufficient data to provide a snapshot.
            </div>
          )}
        </BoxComponent>
      </div>

      <div>
        <div className="p-2 text-end">
          <span
            id="wli-lnk-toggledetails"
            data-testid="wli-lnk-toggledetails"
            className="cursor-pointer text-blue-700"
            onClick={onShowPropertyDetails}
          >
            {showDetails ? ShowHide_MSG.HIDE : ShowHide_MSG.SHOW}
            <i
              id="wli-icon-toggledetails"
              data-testid="wli-icon-toggledetails"
              className={classNames("pi cursor-pointer align-middle", !showDetails ? "pi-angle-down" : "pi-angle-up")}
              style={{ fontSize: "0.5rem", padding: "2px" }}
            ></i>
          </span>
        </div>
      </div>

      {showDetails && (
        <>
          <PropertyDescription propertyDescription={propertyInformation}></PropertyDescription>
          <PropertyAnalysis propertyAnalysis={propertyInformation}></PropertyAnalysis>
          <PropertyDisclaimer isFromAnalyser={propertyInformation.isFromPropertyAnalyser}></PropertyDisclaimer>
        </>
      )}
    </>
  );
});

export default PropertyInformation;
